import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import { useRunBuildGate } from '@/lib/permissions';
import { useRunBuild } from '@/shell/RunBuildContext';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';
import type { Section } from './nav';
import { crumbKind, runBuildHint, type Crumb } from './shellModel';

export type { Crumb } from './shellModel';

function CrumbBody({ crumb: c, last }: { crumb: Crumb; last: boolean }) {
  switch (crumbKind(c, last)) {
    case 'page':
      return <BreadcrumbPage className="truncate font-semibold">{c.label}</BreadcrumbPage>;
    case 'link':
      return (
        <BreadcrumbLink asChild className="truncate">
          <Link to={c.to!}>{c.label}</Link>
        </BreadcrumbLink>
      );
    case 'project':
      return (
        <BreadcrumbLink asChild className="truncate">
          <Link to="/projects/$slug" params={{ slug: c.projectSlug! }}>
            {c.label}
          </Link>
        </BreadcrumbLink>
      );
    case 'home':
      return (
        <BreadcrumbLink asChild className="truncate">
          <Link to="/">{c.label}</Link>
        </BreadcrumbLink>
      );
    default:
      return <span className="truncate">{c.label}</span>;
  }
}

/** On a tablet the org (first crumb, also shown in the sidebar) yields its room to the project and page names. */
const orgOnTablet = (index: number, count: number): boolean => count > 2 && index === 0;
const afterOrgOnTablet = (index: number, count: number): boolean => count > 2 && index === 1;

export function Breadcrumbs({ crumbs, className }: { crumbs: Crumb[]; className?: string }) {
  return (
    <Breadcrumb className={className}>
      <BreadcrumbList className="flex-nowrap gap-2 text-[15px] sm:gap-2">
        {crumbs.map((c, i) => (
          <Fragment key={`${c.label}-${i}`}>
            {i > 0 ? <BreadcrumbSeparator className={cn('text-subtle', afterOrgOnTablet(i, crumbs.length) && 'max-lg:hidden')}>/</BreadcrumbSeparator> : null}
            <BreadcrumbItem className={cn('min-w-0', orgOnTablet(i, crumbs.length) && 'max-lg:hidden')}>
              <CrumbBody crumb={c} last={i === crumbs.length - 1} />
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Opens the Run build sheet (screen 04) with the current project preselected. Hidden for guests and clients; disabled
 * with its reason without `builds:write` or when the API reports no console Run build (`features.runBuild`).
 */
export function RunBuildButton({ size = 'default', variant = 'default', projectSlug, projectId, projectName, className }: { size?: 'default' | 'lg'; variant?: 'default' | 'outline'; projectSlug?: string; projectId?: string; projectName?: string; className?: string }) {
  const { openRunBuild } = useRunBuild();
  const gate = useRunBuildGate(projectId);
  return (
    <GatedButton size={size} variant={variant} className={className} data-testid="run-build-open" can={gate.can} blockedReason={gate.blockedReason} hint={runBuildHint(projectName)} onClick={() => openRunBuild({ projectSlug })}>
      <Icon name="add" size={18} />
      Run build
    </GatedButton>
  );
}

/** One accent button per screen: Run build is the accent only where the page has no main action of its own. */
const RUN_BUILD_IS_PRIMARY: ReadonlySet<Section> = new Set(['overview', 'builds', 'usage']);

export function TopBar({ crumbs, section, projectSlug, projectId, projectName }: { crumbs: Crumb[]; section: Section; projectSlug?: string; projectId?: string; projectName?: string }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 border-b bg-background/85 px-6 backdrop-blur">
      <Breadcrumbs crumbs={crumbs} className="min-w-0 flex-1" />
      <RunBuildButton variant={RUN_BUILD_IS_PRIMARY.has(section) ? 'default' : 'outline'} projectSlug={projectSlug} projectId={projectId} projectName={projectName} />
    </header>
  );
}
